<script setup lang="ts">
import { computed, ref } from "vue";
import { UiText, UiSelect, UiStandardButton, UiInfoBox, UiDialog, UiTextarea, UiAccordion } from "@shopexperts/shared";
import type { AdminPayoutRequest, AdminPayoutRequestsQueryParams, PayoutRequestStatus } from "@shopexperts/legacy-api";
import {
  useAdminPayoutRequests,
  useAdminPayoutRequestActions,
  useAdminPayoutApprovalPreview,
} from "@/app/composables/wallet";
import { formatCurrency } from "@/v2Projects/utils/display";
import { buildAllocationSourceRows } from "@/app/components/expert-manager/wallet/useAdminWalletTransaction";
import AdminTransactionAllocations from "@/app/components/expert-manager/wallet/AdminTransactionAllocations.vue";
import AdminPayoutRequestCard from "./AdminPayoutRequestCard.vue";

const props = defineProps<{
  organizationId?: number | null;
  showOrganization?: boolean;
}>();

const isOrgScoped = computed(() => props.organizationId !== undefined);
const hasOrg = computed(() => typeof props.organizationId === "number" && props.organizationId > 0);

const statusFilter = ref<string>("");

const statusOptions = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Paid", value: "done" },
  { label: "Rejected", value: "rejected" },
];

const filters = computed<AdminPayoutRequestsQueryParams>(() => ({
  ...(hasOrg.value ? { organization_id: props.organizationId as number } : {}),
  ...(statusFilter.value ? { status: statusFilter.value as PayoutRequestStatus } : {}),
}));

const enabled = computed(() => !isOrgScoped.value || hasOrg.value);

const {
  payoutRequests,
  isLoading,
  error,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  refetch,
} = useAdminPayoutRequests({ filters, enabled });

const isEmpty = computed(
  () => !isLoading.value && !error.value && payoutRequests.value.length === 0,
);

const { approve, reject } = useAdminPayoutRequestActions();
const isActing = computed(() => approve.isPending.value || reject.isPending.value);

const approveTarget = ref<AdminPayoutRequest | null>(null);
const approveOpen = ref(false);
const rejectTarget = ref<AdminPayoutRequest | null>(null);
const rejectOpen = ref(false);
const publicNotes = ref("");
const internalNotes = ref("");

const {
  preview,
  isLoading: isPreviewLoading,
  isFetching: isPreviewFetching,
  error: previewError,
  isBlocked: isApprovalBlocked,
  blockedMessage: approvalBlockedMessage,
  refetch: refetchPreview,
} = useAdminPayoutApprovalPreview({
  payoutRequestId: computed(() => approveTarget.value?.id ?? null),
  enabled: approveOpen,
});

const previewCurrency = computed(() => preview.value?.currency ?? approveTarget.value?.currency ?? "USD");

const previewAllocationRows = computed(() =>
  buildAllocationSourceRows(
    preview.value?.available_allocations ?? [],
    (cents) => formatCurrency(cents, previewCurrency.value),
    { prospective: true, subject: "payout" },
  ),
);

const previewUnallocatedLabel = computed(() => {
  const cents = preview.value?.unallocated_available_cents;
  return typeof cents === "number" && cents > 0 ? formatCurrency(cents, previewCurrency.value) : null;
});

const previewSummary = computed(() => {
  const count = previewAllocationRows.value.length;
  if (count === 0) return "No funding source could be matched for this payout";
  const sources = `${count} ${count === 1 ? "source" : "sources"}`;
  return previewUnallocatedLabel.value
    ? `Funded by ${sources}, ${previewUnallocatedLabel.value} unmatched`
    : `Funded by ${sources}`;
});

const previewUnavailableMessage = computed(() => {
  if (!previewError.value || isApprovalBlocked.value) return null;
  return "The funding preview couldn't be loaded. You can still approve; the sources will show on the request afterwards.";
});

const isPreviewBusy = computed(() => isPreviewLoading.value || isPreviewFetching.value);

const canApprove = computed(() => !isPreviewBusy.value && !isApprovalBlocked.value);

function openApprove(request: AdminPayoutRequest) {
  approveTarget.value = request;
  publicNotes.value = "";
  internalNotes.value = "";
  approveOpen.value = true;
}

function openReject(request: AdminPayoutRequest) {
  rejectTarget.value = request;
  publicNotes.value = "";
  internalNotes.value = "";
  rejectOpen.value = true;
}

async function confirmApprove() {
  if (!approveTarget.value || !canApprove.value) return;
  try {
    await approve.mutateAsync({
      payoutRequestId: approveTarget.value.id,
      organizationId: approveTarget.value.organization?.id ?? props.organizationId,
      publicNotes: publicNotes.value.trim() || null,
      internalNotes: internalNotes.value.trim() || null,
    });
    approveOpen.value = false;
  } catch {
    /* Error toast handled by the composable */
  }
}

async function confirmReject() {
  if (!rejectTarget.value) return;
  try {
    await reject.mutateAsync({
      payoutRequestId: rejectTarget.value.id,
      organizationId: rejectTarget.value.organization?.id ?? props.organizationId,
      publicNotes: publicNotes.value.trim() || null,
      internalNotes: internalNotes.value.trim() || null,
    });
    rejectOpen.value = false;
  } catch {
    /* Error toast handled by the composable */
  }
}
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-3">
      <UiText as="h2" variant="h4" text="Payout requests" class="font-semibold text-se-dark" />
      <div class="w-44">
        <UiSelect v-model="statusFilter" :options="statusOptions" placeholder="All statuses" />
      </div>
    </div>

    <UiText
      v-if="isOrgScoped && !hasOrg"
      as="p"
      variant="p"
      text="This expert has no organization yet, so there are no payout requests to show."
      class="text-se-gray-600"
    />

    <template v-else>
      <UiInfoBox v-if="error" variant="error" title="Couldn't load payout requests">
        <div class="flex flex-col items-start gap-3">
          <UiText
            as="p"
            variant="override"
            text="Something went wrong while loading payout requests. Please try again."
            class="text-sm text-se-gray-700"
          />
          <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => refetch()" />
        </div>
      </UiInfoBox>

      <ul v-else-if="isLoading" class="flex flex-col gap-3">
        <li v-for="n in 3" :key="n" class="flex flex-col gap-3 rounded-xl border border-se-gray-200 bg-se-white p-4">
          <div class="flex items-center justify-between">
            <div class="h-4 w-24 animate-pulse rounded bg-se-gray-200" />
            <div class="h-5 w-16 animate-pulse rounded-full bg-se-gray-200" />
          </div>
          <div class="h-3 w-48 animate-pulse rounded bg-se-gray-200" />
        </li>
      </ul>

      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-se-gray-300 bg-se-gray-50 px-6 py-12 text-center"
      >
        <UiText as="p" variant="h5" text="No payout requests" class="font-semibold text-se-dark" />
        <UiText
          as="p"
          variant="override"
          :text="statusFilter ? 'No payout requests match this status.' : 'Payout requests submitted by experts will appear here.'"
          class="max-w-sm text-sm text-se-gray-500"
        />
      </div>

      <template v-else>
        <ul class="flex flex-col gap-3">
          <AdminPayoutRequestCard
            v-for="request in payoutRequests"
            :key="request.id"
            :request="request"
            :show-organization="showOrganization"
            :is-acting="isActing"
            @approve="openApprove"
            @reject="openReject"
          />
        </ul>

        <div v-if="hasNextPage" class="flex justify-center">
          <UiStandardButton
            variant="outline"
            size="sm"
            :label="isFetchingNextPage ? 'Loading…' : 'Load more'"
            :disabled="isFetchingNextPage"
            @click="() => fetchNextPage()"
          />
        </div>
      </template>
    </template>

    <UiDialog
      v-if="approveOpen"
      v-model="approveOpen"
      size="xl"
      title="Approve payout request"
      :text="approveTarget ? `Approve the ${formatCurrency(approveTarget.amount_cents, approveTarget.currency)} payout${approveTarget.organization ? ` for ${approveTarget.organization.name}` : ''}? This debits the organization's wallet and emails the requester.` : ''"
      :primary-action-label="canApprove ? 'Approve payout' : undefined"
      secondary-action-label="Cancel"
      :loading="approve.isPending.value"
      @primary-action="confirmApprove"
      @secondary-action="approveOpen = false"
    >
      <div class="flex flex-col gap-5">
        <div v-if="isPreviewBusy" class="h-4 w-40 animate-pulse rounded bg-se-gray-200" aria-busy="true" aria-label="Loading funding preview" />

        <UiInfoBox v-else-if="isApprovalBlocked" variant="error" title="This payout can't be approved right now">
          <UiText
            as="p"
            variant="override"
            :text="approvalBlockedMessage ?? 'The request is no longer approvable.'"
            class="text-sm text-se-gray-700"
          />
        </UiInfoBox>

        <UiInfoBox v-else-if="previewUnavailableMessage" variant="warning" title="Funding preview unavailable">
          <div class="flex flex-col items-start gap-3">
            <UiText as="p" variant="override" :text="previewUnavailableMessage" class="text-sm text-se-gray-700" />
            <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => refetchPreview()" />
          </div>
        </UiInfoBox>

        <UiAccordion v-else-if="preview">
          <template #trigger>
            <UiText as="span" variant="p-small" :text="previewSummary" class="font-semibold text-se-dark" />
          </template>
          <AdminTransactionAllocations
            :rows="previewAllocationRows"
            :unallocated-label="previewUnallocatedLabel"
            :unallocated-message="previewUnallocatedLabel ? `${previewUnallocatedLabel} of this payout has no matching funding source yet. Approving still works; reconcile the wallet afterwards to close the gap.` : null"
            subject="payout"
            hide-heading
            flush
            class="border-t-0 pt-3"
          />
        </UiAccordion>

        <div class="flex flex-col gap-1.5">
          <UiText as="label" variant="label" text="Note to expert (optional)" />
          <UiTextarea
            v-model="publicNotes"
            :rows="2"
            placeholder="Shown on the payout transaction and in the approval email..."
            :disabled="approve.isPending.value"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <UiText as="label" variant="label" text="Internal note (optional)" />
          <UiTextarea
            v-model="internalNotes"
            :rows="2"
            placeholder="Only visible to admins..."
            :disabled="approve.isPending.value"
          />
        </div>
      </div>
    </UiDialog>

    <UiDialog
      v-if="rejectOpen"
      v-model="rejectOpen"
      title="Reject payout request"
      :text="rejectTarget ? `Reject the ${formatCurrency(rejectTarget.amount_cents, rejectTarget.currency)} payout${rejectTarget.organization ? ` for ${rejectTarget.organization.name}` : ''}? The requester will be emailed.` : ''"
      primary-action-label="Reject payout"
      primary-action-variant="danger"
      secondary-action-label="Cancel"
      :loading="reject.isPending.value"
      @primary-action="confirmReject"
      @secondary-action="rejectOpen = false"
    >
      <div class="flex flex-col gap-5">
        <div class="flex flex-col gap-1.5">
          <UiText as="label" variant="label" text="Note to expert (optional)" />
          <UiTextarea
            v-model="publicNotes"
            :rows="2"
            placeholder="Shared with the expert in the rejection email..."
            :disabled="reject.isPending.value"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <UiText as="label" variant="label" text="Internal note (optional)" />
          <UiTextarea
            v-model="internalNotes"
            :rows="2"
            placeholder="Only visible to admins..."
            :disabled="reject.isPending.value"
          />
        </div>
      </div>
    </UiDialog>
  </section>
</template>
